import {useCallback, useEffect, useMemo} from "react";
import {isValid, parse} from "date-fns";
import MarkerWithInfoWindow from "../../../../../../../components/map/markers/marker-with-info-window.jsx";
import {useGarrisonTimelineGridStore} from "../../../../hooks/use-garrison-timeline-grid-store.js";
import {TABLE_MODES} from "../../../../utils/garrison-timeline-utils.js";
import {navigateHook} from "../../../../../../../hooks/use-navigate.js";
import {useFitBounds} from "../../../../../../../hooks/map-hooks/viewport-hooks/use-fit-bounds.js";
import GarrisonTimelineIcon from "../garrison-timeline-icon.jsx";
import Button from "../../../../../../../components/button/button.jsx";
import {buildGarrisonRouteUrl} from "../../../../utils/garrison-timeline-utils.js";
import SRoute from "../../../../../../../components/icons/route.jsx";
import {FaXmark} from "react-icons/fa6";

const infoWindowContent = (item, navigate, filter, dispatchesById, onClose) => {
  const formattedDate = item?.date ? item.date.slice(0, 16).replace(" ", " - ") : "";
  const garrisonFunction = item?.garrison?.intendedFunction || item?.garrison?.role;
  const garrisonFunctionLabels = {
    ADMINISTRATION: "Administração",
    FIXED_LOCATION: "Posto fixo",
    PATROLLING: "Patrulhamento"
  };

  let statusLabel = "Disponível";
  let statusColor = "bg-[#808080] text-white";

  if (item?.status === "OFFLINE") {
    statusLabel = "Offline";
    statusColor = "bg-[repeating-linear-gradient(45deg,#4B5563,#4B5563_4px,#DC2626_4px,#DC2626_5px)] text-white";
  } else if (item?.dispatch) {
    const dispatch = dispatchesById.get(String(item.dispatch.id)) || item.dispatch;
    const isActivity = dispatch?.dispatchActivity || dispatch?.dispatchNature?.activity || String(dispatch?.dispatchNature || "").toUpperCase() === "ATIVIDADE";

    statusLabel = isActivity ? "Em atividade" : "Despachado";
    statusColor = isActivity ? "bg-[#287499] text-white" : "bg-[#008C51] text-white";
  }

  return (
    <div className="flex w-[340px] max-w-[calc(100vw-48px)] flex-col gap-2.5 font-sans text-[13px] text-gray-800 mt-1">
      <div className="flex items-center gap-2">
        <div className="bg-[#e3f2fd] text-[#063a6c] px-2 py-1 rounded font-semibold whitespace-nowrap">
          {formattedDate}
        </div>
        <div className={`${statusColor} px-2 py-1 rounded font-bold text-[11px] tracking-wide`}>
          {statusLabel}
        </div>
        <Button
          title="Visualizar rota completa"
          className="ml-auto shrink-0 justify-self-end"
          onClick={() => {
            const routeUrl = buildGarrisonRouteUrl(item?.garrison?.id, filter, item.originalIndex + 1);

            if (routeUrl) navigate(routeUrl, true);
          }}
        >
          <SRoute/>
        </Button>
        <button className="w-[32px] h-[32px] ml-auto cursor-pointer" onClick={onClose} title="Fechar">
          <FaXmark
            size={24}
          />
        </button>
      </div>

      {item?.garrison?.name && (
        <div
          className="border border-[#1588F3] text-[#1588F3] p-2 rounded cursor-pointer font-medium hover:bg-blue-50 transition-colors"
          onClick={() => navigate(`/despacho/garrison/form/${item.garrison.id}`, `/despacho/garrison/${item.garrison.id}/edit`, true)}
        >
          {item.garrison.name}
        </div>
      )}

      <div className="flex flex-col gap-0.5 mt-1">
        <div><span className="text-gray-500">Função:</span> <span className="font-bold">{garrisonFunctionLabels[garrisonFunction] || garrisonFunction || "-"}</span></div>
        <div><span className="text-gray-500">Equipe:</span> <span className="font-bold">{item?.garrison?.team || "-"}</span></div>
      </div>

      <div className="w-full h-px bg-gray-200 my-0.5" />

      <div className="flex flex-col gap-0.5">
        <div>
          <span className="text-gray-500">Despacho ID:</span>{" "}
          {item?.dispatch?.id ? (
            <span
              className="font-bold cursor-pointer text-[#1588F3] hover:underline"
              onClick={() => navigate(`/despacho/dispatch/form/${item.dispatch.id}`, `/despacho/dispatch/${item.dispatch.id}/edit`, true)}
            >
              {item.dispatch.id}
            </span>
          ) : (
            <span className="font-bold">-</span>
          )}
        </div>
        <div><span className="text-gray-500">Natureza:</span> <span className="font-bold">{item?.dispatch?.dispatchNature || "-"}</span></div>
      </div>

      <div className="w-full h-px bg-gray-200 my-0.5" />

      <div className="flex flex-col gap-0.5">
        <div><span className="text-gray-500">Local:</span> <span className="font-bold">{item?.dispatch?.factPlace || "-"}</span></div>
        <div className="leading-tight"><span className="text-gray-500">Endereço:</span> <span className="font-bold">{item?.dispatch?.address || "-"}</span></div>
      </div>

      <div className="w-full h-px bg-gray-200 my-0.5" />

      <div>
        <span className="text-gray-500">BO GCM:</span>{" "}
        {item?.dispatch?.bo ? (
          <span
            className="font-bold cursor-pointer text-[#1588F3] hover:underline"
            onClick={() => navigate(`/bo/show/${item.dispatch.bo.replace("/", "-")}`, `/bo/show/${item.dispatch.bo.replace("/", "-")}`, true)}
          >
            {item.dispatch.bo}
          </span>
        ) : (
          <span className="font-bold">-</span>
        )}
      </div>
    </div>
  );
};

const GarrisonTimelineMapContent = ({garrisonsList, timelineData, dispatches, elapsedSeconds, filter, snapshot, onActiveGarrisonsChange}) => {
  const {gridApi, selectionMode, setSelectionMode, activeMarkerKey, setActiveMarkerKey} = useGarrisonTimelineGridStore();
  const navigate = navigateHook();
  const dispatchesById = useMemo(
    () => new Map((dispatches || []).map(({
      dispatchId,
      dispatchActivity,
      dispatchNature,
      dispatchFactPlace,
      dispatchOccurrenceNumber,
      dispatchAddress
    }) => [
      String(dispatchId),
      {
        dispatchActivity,
        dispatchNature,
        factPlace: dispatchFactPlace,
        bo: dispatchOccurrenceNumber,
        address: dispatchAddress
      }
    ])),
    [dispatches]
  );

  const onCloseClick = useCallback(() => setActiveMarkerKey(null), [setActiveMarkerKey]);
  const onToggleClick = useCallback((item) => {
    if (activeMarkerKey === item.id) {
      onCloseClick();
      return;
    }

    if (selectionMode === TABLE_MODES.SOLVE_CLUSTER && gridApi) {
      setSelectionMode(TABLE_MODES.SELECT_ITEMS);
      gridApi.deselectAll();
    }

    setActiveMarkerKey(item.id);
  }, [activeMarkerKey, gridApi, onCloseClick, selectionMode, setActiveMarkerKey, setSelectionMode]);

  const activeMarkersData = useMemo(() => {
    if (snapshot) {
      return snapshot.map((item) => {
        const garrisonIndex = garrisonsList?.findIndex(garrison => String(garrison.id) === String(item.garrisonId));
        const originalIndex = garrisonIndex >= 0 ? garrisonsList[garrisonIndex].originalIndex ?? garrisonIndex : 0;

        return {
          id: item.garrisonId,
          lat: item.latitude,
          lng: item.longitude,
          date: item.time,
          originalIndex,
          garrison: {
            id: item.garrisonId,
            name: item.garrisonName,
            team: item.garrisonTeam,
            intendedFunction: item.garrisonIntendedFunction
          },
          dispatch: item.dispatchId ? {
            id: item.dispatchId,
            dispatchActivity: item.dispatchActivity,
            dispatchNature: item.dispatchNature,
            factPlace: item.dispatchFactPlace,
            bo: item.dispatchOccurrenceNumber,
            address: item.dispatchAddress
          } : null,
          status: item.status
        };
      }).filter(item => item.lat && item.lng);
    }

    if (!timelineData || !garrisonsList) return [];

    const currentMinute = Math.floor((elapsedSeconds || 0) / 60);

    return garrisonsList.map((garrison, index) => {
      const routePoints = timelineData[garrison.id];

      if (!routePoints || routePoints.length === 0) return null;

      const currentPoint = routePoints[currentMinute];

      if (!currentPoint) return null;

      return {
        ...currentPoint,
        id: garrison.id,
        lat: currentPoint.latitude,
        lng: currentPoint.longitude,
        garrison,
        dispatch: currentPoint.dispatchId
          ? {id: currentPoint.dispatchId, ...dispatchesById.get(String(currentPoint.dispatchId))}
          : null,
        date: currentPoint.time,
        originalIndex: index
      };
    }).filter(item => item !== null && item.lat && item.lng);
  }, [garrisonsList, timelineData, elapsedSeconds, snapshot]);

  useFitBounds(activeMarkersData, {bottom: 120});

  useEffect(() => {
    if (onActiveGarrisonsChange) {
      onActiveGarrisonsChange(activeMarkersData.map(item => item.id));
    }
  }, [activeMarkersData, onActiveGarrisonsChange]);

  return activeMarkersData.map(item => (
    <MarkerWithInfoWindow
      key={item.id}
      itemId={item}
      position={{lat: item.lat, lng: item.lng}}
      isOpen={activeMarkerKey === item.id}
      onClose={onCloseClick}
      onToggleClick={() => onToggleClick(item)}
      infoWindowContent={infoWindowContent(item, navigate, filter, dispatchesById, onCloseClick)}
    >
      <GarrisonTimelineIcon i={item.originalIndex + 1} />
    </MarkerWithInfoWindow>
  ));
};

export default GarrisonTimelineMapContent;
